import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth, firestore } from './firebase.js';
import { doc, getDoc } from 'firebase/firestore';
import './Sidebar.css';
import ThemeToggle from './ThemeToggle.js';
import Selector from './Selector.js';

import {
  Package, Crown, Zap, Award, LogOut, Swords,
  PanelLeftClose, PanelLeftOpen, Clock, ChevronDown,
  Trophy, ScrollText
} from 'lucide-react';

import { useAuth } from './AuthContext.js';
import { useChallengeStorage } from './hooks/useChallengeStorage.js';

const Sidebar = ({
  sidebarOpen,
  setSidebarOpen,
  currentTopic,
  setCurrentTopic,
  currentDifficulty,
  setCurrentDifficulty,
  topics
}) => {

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { userPlan, PLANS } = useChallengeStorage();

  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [profilePicUrl, setProfilePicUrl]     = useState('');
  const [imageError, setImageError]           = useState(false);
  const profileRef = useRef(null);

  useEffect(() => {
    const fetchProfilePic = async () => {
      if (!user) { setProfilePicUrl(''); return; }
      try {
        const snap = await getDoc(doc(firestore, 'users', user.uid));
        setProfilePicUrl(
          snap.exists()
            ? (snap.data().photoURL || user.photoURL || '')
            : (user.photoURL || '')
        );
      } catch {
        setProfilePicUrl(user.photoURL || '');
      }
    };
    fetchProfilePic();
  }, [user, user?.photoURL]);

  useEffect(() => { setImageError(false); }, [profilePicUrl]);

  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = async () => {
    await signOut(auth);
    setProfileMenuOpen(false);
    navigate('/tasks');
  };

  const getInitials = (email) => email?.[0]?.toUpperCase() || 'U';

  const getPlanIcon = () => {
    switch (userPlan) {
      case 'premium': return <Crown size={14} />;
      case 'pro':     return <Award size={14} />;
      case 'starter': return <Zap size={14} />;
      default:        return <Package size={14} />;
    }
  };

  const getPlanColor = () => {
    switch (userPlan) {
      case 'premium': return '#FFD700';
      case 'pro':     return '#9333EA';
      case 'starter': return '#3B82F6';
      default:        return '#6B7280';
    }
  };

  // ── Navigation ─────────────────────────────────────────────────────────────
  const NAV_GROUPS = [
    {
      label: 'Play',
      items: [
        { id: 'challenges', name: 'Solo',        icon: <Zap size={18} />,        path: '/challenges' },
        { id: 'battle',     name: '1v1 Battle',  icon: <Swords size={18} />,     path: '/battle' },
        { id: 'leaderboard',name: 'Leaderboard', icon: <Trophy size={18} />,     path: '/leaderboard' },
      ],
    },
    {
      label: 'Progress',
      items: [
        { id: 'sessions',   name: 'Sessions',    icon: <ScrollText size={18} />, path: '/dashboard/sessions' },
        { id: 'badges',     name: 'Badges',      icon: <Award size={18} />,      path: '/dashboard/badges' },
      ],
    },
  ];

  const currentPath = location.pathname;
  const showProfileImage = profilePicUrl && !imageError;
  const currentPlan = PLANS?.[userPlan];

  return (
    <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>

      {/* ── Header ── */}
      <div className="sidebar-header">
        <div className="sidebar-brand">
          <img
            src="/logo192.png"
            alt="logo"
            className="sidebar-logo-img"
          />
          {sidebarOpen && <span className="sidebar-brand-name">Prompttar.ai</span>}
        </div>
        <button className="sidebar-toggle-btn" onClick={() => setSidebarOpen(v => !v)}>
          {sidebarOpen ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
        </button>
      </div>

      {/* ── Filters (ChallengesPage only) ── */}
      {currentTopic !== undefined && setCurrentTopic && topics && (
        <div className="sidebar-filters">
          <Selector
            label="Topics"
            value={currentTopic}
            onChange={setCurrentTopic}
            options={topics}
          />
          <Selector
            label="Difficulty"
            value={currentDifficulty}
            onChange={setCurrentDifficulty}
            options={['All', 'Very Easy', 'Easy', 'Medium', 'Hard', 'Expert']}
          />
        </div>
      )}

      {/* ── Navigation ── */}
      <nav className="navigation">
        {NAV_GROUPS.map((group) => (
          <div key={group.label} className="nav-group">

            {/* Group label — only visible when sidebar is open */}
            {sidebarOpen && (
              <span className="nav-group-label">{group.label}</span>
            )}

            {group.items.map((item) => (
              <button
                key={item.id}
                onClick={() => navigate(item.path)}
                className={`nav-item ${currentPath === item.path ? 'active' : ''}`}
                data-tooltip={item.name}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.name}</span>
              </button>
            ))}
          </div>
        ))}
      </nav>

      {/* ── Upgrade CTA ── */}
      {user && userPlan === 'free' && (
        <div className="sidebar-upgrade-wrap">
          <button className="sidebar-upgrade-btn" onClick={() => navigate('/pricing')}>
            <Crown size={14} />
            <span className="upgrade-label">Upgrade</span>
          </button>
        </div>
      )}

      {/* ── Profile footer ── */}
      {user && (
        <div className="sidebar-profile-footer" ref={profileRef}>
          <button className="sidebar-avatar-btn" onClick={() => setProfileMenuOpen(v => !v)}>
            <div className="sidebar-avatar-circle">
              {showProfileImage
                ? <img src={profilePicUrl} alt="" onError={() => setImageError(true)} />
                : getInitials(user.email)
              }
            </div>
            <span className="sidebar-avatar-email">{user.email}</span>
            <ChevronDown size={14} className={`spp-chevron ${profileMenuOpen ? 'flipped' : ''}`} />
          </button>

          {profileMenuOpen && (
            <div className="spp-popover">
              <div className="spp-user-row">
                <div className="spp-avatar">
                  {showProfileImage
                    ? <img src={profilePicUrl} alt="" />
                    : getInitials(user.email)
                  }
                </div>
                <div className="spp-user-info">
                  <p className="spp-email">{user.email}</p>
                  <p className="spp-plan" style={{ color: getPlanColor() }}>
                    {getPlanIcon()}&nbsp;{currentPlan?.name || 'Free'}
                  </p>
                </div>
              </div>

              <div className="spp-divider" />

              <Link to="/profile" className="spp-item" onClick={() => setProfileMenuOpen(false)}>
                Profile
              </Link>
              <Link to="/pricing" className="spp-item" onClick={() => setProfileMenuOpen(false)}>
                Pricing
              </Link>
                <ThemeToggle />

             

              <button className="spp-item spp-logout" onClick={handleLogout}>
                <LogOut size={14} />&nbsp;Logout
              </button>
            </div>
          )}
        </div>
      )}
    </aside>
  );
};

export default Sidebar;